import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
    Play,
    Clock,
    Minus,
    Plus,
    Trash2,
    Archive,
    Pencil,
    Check,
    X,
    ChevronDown,
    ChevronUp,
    MessageSquare,
    Send,
} from "lucide-react";
import type { Task } from "@/types";
import { TaskComments } from "@/components/TaskComments";
import { TagDisplay, TagInput } from "@/components/TagInput";

interface PendingTaskItemProps {
    task: Task;
    onStart: () => void;
    onDelete: () => void;
    onAddTime: (minutes: number) => void;
    onMoveToBacklog?: () => void;
    onRename?: (newName: string) => void;
    canStart: boolean;
    onEditComment?: (commentId: string, newText: string) => void;
    onDeleteComment?: (commentId: string) => void;
    onUpdateTags?: (tags: string[]) => void;
    onAddComment?: (text: string) => void;
    allTags?: string[]; // All existing tags for autocomplete
}

export function PendingTaskItem({
    task,
    onStart,
    onDelete,
    onAddTime,
    onMoveToBacklog,
    onRename,
    canStart,
    onEditComment,
    onDeleteComment,
    onUpdateTags,
    onAddComment,
    allTags = [],
}: PendingTaskItemProps) {
    const [isEditing, setIsEditing] = useState(false);
    const [editedName, setEditedName] = useState(task.name);
    const [showDetails, setShowDetails] = useState(false);
    const [showTagEdit, setShowTagEdit] = useState(false);
    const [showAddComment, setShowAddComment] = useState(false);
    const [newComment, setNewComment] = useState("");

    const handleAddComment = () => {
        if (newComment.trim() && onAddComment) {
            onAddComment(newComment.trim());
            setNewComment("");
            setShowAddComment(false);
        }
    };

    const handleSaveEdit = () => {
        if (editedName.trim() && onRename) {
            onRename(editedName.trim());
        }
        setIsEditing(false);
    };

    const handleCancelEdit = () => {
        setEditedName(task.name);
        setIsEditing(false);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter") {
            handleSaveEdit();
        } else if (e.key === "Escape") {
            handleCancelEdit();
        }
    };

    return (
        <div className="group p-4 rounded-xl border border-border bg-card hover:border-primary/30 hover:shadow-md transition-all">
            <div className="flex items-center gap-4">
                <div className="shrink-0">
                    <Button
                        size="icon"
                        variant={canStart ? "default" : "secondary"}
                        onClick={onStart}
                        disabled={!canStart}
                        title={
                            !canStart ? "Pause active task first" : "Start task"
                        }
                        className="h-10 w-10 rounded-full"
                    >
                        <Play className="w-4 h-4" />
                    </Button>
                </div>
                <div className="flex-1 min-w-0">
                    {isEditing ? (
                        <div className="flex items-center gap-2">
                            <Input
                                value={editedName}
                                onChange={(e) => setEditedName(e.target.value)}
                                onKeyDown={handleKeyDown}
                                className="h-8"
                                autoFocus
                            />
                            <Button
                                size="icon"
                                variant="ghost"
                                onClick={handleSaveEdit}
                                className="h-8 w-8 text-green-600"
                            >
                                <Check className="w-4 h-4" />
                            </Button>
                            <Button
                                size="icon"
                                variant="ghost"
                                onClick={handleCancelEdit}
                                className="h-8 w-8 text-muted-foreground"
                            >
                                <X className="w-4 h-4" />
                            </Button>
                        </div>
                    ) : (
                        <>
                            <div
                                className="font-medium break-words cursor-pointer hover:text-primary"
                                onClick={() => onRename && setIsEditing(true)}
                                title="Click to edit"
                            >
                                {task.name}
                            </div>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground mt-0.5">
                                <Clock className="w-3 h-3" />
                                <span>{task.estimatedMinutes} min</span>
                                {task.tags && task.tags.length > 0 && (
                                    <TagDisplay tags={task.tags} size="sm" />
                                )}
                                {(onUpdateTags ||
                                    (task.comments &&
                                        task.comments.length > 0) ||
                                    (task.tags && task.tags.length > 0)) && (
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        className="h-5 px-1 text-xs"
                                        onClick={() =>
                                            setShowDetails(!showDetails)
                                        }
                                    >
                                        {showDetails ? (
                                            <ChevronUp className="w-3 h-3" />
                                        ) : (
                                            <ChevronDown className="w-3 h-3" />
                                        )}
                                    </Button>
                                )}
                            </div>
                        </>
                    )}
                </div>
                {!isEditing && (
                    <>
                        <div className="hidden sm:flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button
                                size="icon"
                                variant="ghost"
                                onClick={() => onAddTime(-5)}
                                disabled={task.estimatedMinutes <= 5}
                                className="h-8 w-8"
                            >
                                <Minus className="w-3 h-3" />
                            </Button>
                            <Badge
                                variant="secondary"
                                className="min-w-12 justify-center"
                            >
                                {task.estimatedMinutes}m
                            </Badge>
                            <Button
                                size="icon"
                                variant="ghost"
                                onClick={() => onAddTime(5)}
                                className="h-8 w-8"
                            >
                                <Plus className="w-3 h-3" />
                            </Button>
                        </div>
                        <div className="flex items-center gap-1 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                            {onRename && (
                                <Button
                                    size="icon"
                                    variant="ghost"
                                    onClick={() => setIsEditing(true)}
                                    className="h-8 w-8 text-muted-foreground hover:text-primary"
                                    title="Edit name"
                                >
                                    <Pencil className="w-4 h-4" />
                                </Button>
                            )}
                            {onMoveToBacklog && (
                                <Button
                                    size="icon"
                                    variant="ghost"
                                    onClick={onMoveToBacklog}
                                    className="h-8 w-8 text-amber-600 hover:text-amber-700 hover:bg-amber-100 dark:hover:bg-amber-900/30"
                                    title="Move to Backlog"
                                >
                                    <Archive className="w-4 h-4" />
                                </Button>
                            )}
                            <Button
                                size="icon"
                                variant="ghost"
                                onClick={onDelete}
                                className="h-8 w-8 text-muted-foreground hover:text-destructive"
                                title="Delete"
                            >
                                <Trash2 className="w-4 h-4" />
                            </Button>
                        </div>
                    </>
                )}
            </div>

            {/* Expanded Details Section */}
            {showDetails && (
                <div className="mt-4 pt-4 border-t space-y-3">
                    {/* Tag Editor */}
                    {onUpdateTags && (
                        <div>
                            {showTagEdit ? (
                                <div className="space-y-2">
                                    <TagInput
                                        tags={task.tags || []}
                                        onChange={(tags) => {
                                            onUpdateTags(tags);
                                        }}
                                        placeholder="Add tags..."
                                        allTags={allTags}
                                    />
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        className="h-6 text-xs"
                                        onClick={() => setShowTagEdit(false)}
                                    >
                                        Done
                                    </Button>
                                </div>
                            ) : (
                                <div
                                    className="flex items-center gap-2 cursor-pointer"
                                    onClick={() => setShowTagEdit(true)}
                                >
                                    {task.tags && task.tags.length > 0 ? (
                                        <TagDisplay tags={task.tags} />
                                    ) : (
                                        <span className="text-xs text-muted-foreground hover:text-foreground">
                                            + Add tags
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Comments Section */}
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                                <MessageSquare className="w-3 h-3" />
                                Notes
                            </span>
                            {onAddComment && !showAddComment && (
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-5 text-xs px-1"
                                    onClick={() => setShowAddComment(true)}
                                >
                                    + Add
                                </Button>
                            )}
                        </div>

                        {showAddComment && onAddComment && (
                            <div className="flex gap-2 mb-2">
                                <Input
                                    placeholder="Add a note..."
                                    value={newComment}
                                    onChange={(e) =>
                                        setNewComment(e.target.value)
                                    }
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter")
                                            handleAddComment();
                                        if (e.key === "Escape") {
                                            setShowAddComment(false);
                                            setNewComment("");
                                        }
                                    }}
                                    className="h-8 text-sm"
                                    autoFocus
                                />
                                <Button
                                    size="icon"
                                    onClick={handleAddComment}
                                    disabled={!newComment.trim()}
                                    className="h-8 w-8"
                                >
                                    <Send className="w-3 h-3" />
                                </Button>
                            </div>
                        )}

                        {task.comments &&
                        task.comments.length > 0 &&
                        onEditComment &&
                        onDeleteComment ? (
                            <TaskComments
                                comments={task.comments}
                                onEdit={onEditComment}
                                onDelete={onDeleteComment}
                                maxVisible={3}
                            />
                        ) : (
                            !showAddComment && (
                                <p className="text-xs text-muted-foreground">
                                    No notes yet
                                </p>
                            )
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
